import React, {PropsWithChildren, useState} from 'react';
import {API} from '@chekin/api';
import {
  getThrottleWaitSeconds,
  isShortLink,
  LocalStorage,
  type OldShortLink,
  QUERY_CACHE_KEYS,
  RESERVATION_SEARCH_METHOD,
  RESERVATION_SEARCH_MODE,
  resolveLinkType,
  resolveObjectDetails,
  resolveShortLinkIds,
  setFeatureFlags,
  SHORT_LINK_TYPE,
  type ShortLink,
  MAIN_TOKEN_ID_KEY,
  persistResolvedIds,
  RESERVATION_ID_KEY,
} from '@chekin/core';
import {queryOptions, useQuery} from '@tanstack/react-query';
import {useLocation} from 'react-router-dom';
import {useRedirectToUrlFromShortLink} from 'hooks/useRedirectToUrlFromShortLink';
import {getReservationFromStorage, getTemplateId} from './utils';

type ContextProps = {
  isTokenValid: boolean;
  isAuthInitialization: boolean;
  isAuthInitializationError: boolean;
  isThrottled: boolean;
  throttleWaitSeconds?: number;
  isSuccessSDK: boolean;
  foundReservationId?: string;
  mainToken?: string | null;
  hasToken?: boolean;
  objectId?: string;
  modelName?: string;
  link?: string;
  redirectTo?: string;
  linkType?: SHORT_LINK_TYPE;
  templateId?: string | null;
  reservationSearchMode?: RESERVATION_SEARCH_MODE;
  reservationSearchMethods?: RESERVATION_SEARCH_METHOD[];
  housingSearchEnabled?: boolean;
  accountName?: string;
};

const ChekinSDKContext = React.createContext<ContextProps>({} as ContextProps);

const getShortLinkQueryOptions = (shortToken: string) =>
  queryOptions<OldShortLink | ShortLink, Error>({
    queryKey: [QUERY_CACHE_KEYS.apiClient, shortToken],
    queryFn: () => API.init(shortToken),
  });

function ChekinSDKProvider({children}: PropsWithChildren) {
  const [foundReservationId, setFoundReservationId] = useState<string | undefined>(
    getReservationFromStorage,
  );

  const {pathname} = useLocation();
  const tokenFromUrl = pathname.split('/').filter(Boolean)[0] || '';
  const shortToken = tokenFromUrl !== 'oauth' ? tokenFromUrl : '';

  const {
    data,
    error,
    isLoading: isAuthInitialization,
    isLoadingError: isAuthInitializationError,
    isSuccess: isSuccessSDK,
  } = useQuery({
    ...getShortLinkQueryOptions(shortToken),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: Boolean(shortToken),
    refetchOnReconnect: false,
  });

  const isTokenValid = Boolean(data);
  const templateId = getTemplateId(data);

  React.useMemo(() => {
    const redesignEnabled = (data as ShortLink | undefined)?.guestapp_redesign_enabled;
    if (redesignEnabled !== undefined) {
      setFeatureFlags({GUESTAPP_REDESIGN_ENABLED: redesignEnabled});
    }
  }, [data]);

  React.useEffect(() => {
    if (!isShortLink(data)) return;

    const ids = resolveShortLinkIds(data);
    setFoundReservationId(ids.reservationId);
    persistResolvedIds(ids);
    if (!ids.reservationId) {
      LocalStorage.remove(RESERVATION_ID_KEY);
    }
  }, [data]);

  const linkType = React.useMemo(() => resolveLinkType(data), [data]);

  useRedirectToUrlFromShortLink({
    linkType,
    token: shortToken,
  });

  const isThrottled =
    isAuthInitializationError && (error as {status?: number})?.status === 429;

  const throttleWaitSeconds = isThrottled
    ? getThrottleWaitSeconds(error) ?? undefined
    : undefined;

  const mainToken = React.useMemo(() => {
    if (shortToken) {
      const previousToken = LocalStorage.get(MAIN_TOKEN_ID_KEY);
      if (previousToken && previousToken !== shortToken) {
        LocalStorage.remove(RESERVATION_ID_KEY);
      }
      LocalStorage.set(MAIN_TOKEN_ID_KEY, shortToken);
    }

    return shortToken || LocalStorage.get(MAIN_TOKEN_ID_KEY);
  }, [shortToken]);

  const objectDetails = React.useMemo(() => resolveObjectDetails(data), [data]);

  const contextValue = React.useMemo<ContextProps>(
    () => ({
      isTokenValid,
      isAuthInitialization,
      isAuthInitializationError,
      isThrottled,
      throttleWaitSeconds,
      foundReservationId,
      mainToken,
      isSuccessSDK,
      objectId: objectDetails.id,
      modelName: objectDetails.model,
      link: objectDetails.link,
      redirectTo: objectDetails.redirectTo,
      reservationSearchMode: (objectDetails.searchMode ||
        RESERVATION_SEARCH_MODE.both) as RESERVATION_SEARCH_MODE,
      reservationSearchMethods: objectDetails.reservationSearchMethods,
      housingSearchEnabled: objectDetails.housingSearchEnabled,
      accountName: objectDetails.accountName,
      linkType,
      hasToken: Boolean(shortToken),
      templateId,
    }),
    [
      linkType,
      isAuthInitialization,
      isAuthInitializationError,
      isThrottled,
      throttleWaitSeconds,
      isSuccessSDK,
      isTokenValid,
      mainToken,
      foundReservationId,
      objectDetails,
      shortToken,
      templateId,
    ],
  );

  return <ChekinSDKContext.Provider value={contextValue} children={children} />;
}

export {ChekinSDKContext, ChekinSDKProvider, getShortLinkQueryOptions};
